import type { FeedbackRecord, PaginatedResponse, Project, ProjectOrigin } from '@feedback-system/types';

const WORKER_URL = import.meta.env.VITE_WORKER_URL || 'http://localhost:8787';
const DEV_TOKEN = import.meta.env.VITE_DASHBOARD_DEV_TOKEN || '';

function getAuthHeaders() {
  return {
    'Content-Type': 'application/json',
    ...(DEV_TOKEN ? { 'Authorization': `Bearer ${DEV_TOKEN}` } : {}),
  };
}

export async function fetchFeedback(
  projectId?: string,
  status?: string,
  severity?: string,
  page?: number,
  limit?: number
): Promise<PaginatedResponse<FeedbackRecord>> {
  const params = new URLSearchParams();
  if (projectId) params.append('projectId', projectId);
  if (status) params.append('status', status);
  if (severity) params.append('severity', severity);
  if (page) params.append('page', String(page));
  if (limit) params.append('limit', String(limit));

  const response = await fetch(`${WORKER_URL}/feedback?${params}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch feedback: ${response.statusText}`);
  }

  return response.json();
}

export async function fetchFeedbackDetail(feedbackId: string): Promise<FeedbackRecord> {
  const response = await fetch(`${WORKER_URL}/feedback/${feedbackId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch feedback: ${response.statusText}`);
  }

  return response.json();
}

export async function updateFeedback(
  feedbackId: string,
  updates: { status?: string; reanalyze?: boolean }
): Promise<FeedbackRecord> {
  const response = await fetch(`${WORKER_URL}/feedback/${feedbackId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error(`Failed to update feedback: ${response.statusText}`);
  }

  return response.json();
}

export function getScreenshotUrl(feedbackId: string): string {
  return `${WORKER_URL}/screenshots/${feedbackId}`;
}

// --- Projects ---------------------------------------------------------

export async function fetchProjects(): Promise<Project[]> {
  const response = await fetch(`${WORKER_URL}/projects`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch projects: ${response.statusText}`);
  }

  const data = (await response.json()) as { items: Project[] };
  return data.items;
}

export async function createProject(name: string): Promise<Project & { token: string }> {
  const response = await fetch(`${WORKER_URL}/projects`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error || `Failed to create project: ${response.statusText}`);
  }

  return response.json();
}

export async function rotateProjectToken(projectId: string): Promise<Project & { token: string }> {
  const response = await fetch(`${WORKER_URL}/projects/${projectId}/rotate-token`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to rotate token: ${response.statusText}`);
  }

  return response.json();
}

export async function fetchProjectOrigins(projectId: string): Promise<ProjectOrigin[]> {
  const response = await fetch(`${WORKER_URL}/projects/${projectId}/origins`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch origins: ${response.statusText}`);
  }

  const data = (await response.json()) as { items: ProjectOrigin[] };
  return data.items;
}

export async function addProjectOrigin(projectId: string, origin: string): Promise<ProjectOrigin> {
  const response = await fetch(`${WORKER_URL}/projects/${projectId}/origins`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ origin }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error || `Failed to add origin: ${response.statusText}`);
  }

  return response.json();
}

export async function deleteProjectOrigin(projectId: string, originId: string): Promise<void> {
  const response = await fetch(`${WORKER_URL}/projects/${projectId}/origins/${originId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok && response.status !== 204) {
    throw new Error(`Failed to remove origin: ${response.statusText}`);
  }
}
